/**
 * SDK 메시지 통신 타입 정의
 *
 * SDK와 chat-app이 postMessage로 주고받는 메시지 형태를 정의합니다.
 */

import type {
  AICXClientContext,
  AICXDeliveryAddressEditData,
  SaaSWidgetDisplayConfig,
} from './config';

// ========================================
// 메시지 소스 식별자
// ========================================

/**
 * SDK에서 보내는 메시지에 붙이는 식별자
 * chat-app이 이 값으로 "아, SDK에서 보낸 메시지구나" 판단
 */
export const SDK_SOURCE = 'aicx-sdk' as const;

/**
 * chat-app에서 보내는 메시지에 붙이는 식별자
 * SDK가 이 값으로 "아, chat-app에서 보낸 메시지구나" 판단
 */
export const APP_SOURCE = 'aicx-chat-app' as const;

// ========================================
// 메시지 타입 상수
// ========================================

/**
 * 모든 메시지 타입을 상수로 정의
 * 오타 방지 + IDE 자동완성 지원
 */
export const MESSAGE_TYPES = {
  // SDK → chat-app 방향
  SDK_INIT: 'SDK_INIT', // SDK 초기화 정보 전달
  SDK_THEME_CHANGE: 'SDK_THEME_CHANGE', // 테마 변경 요청
  SDK_PING: 'SDK_PING', // 연결 확인 (살아있니?)
  SDK_SEND_MESSAGE: 'SDK_SEND_MESSAGE', // 채팅 메시지 전송 요청
  SDK_SESSION_UPDATE: 'SDK_SESSION_UPDATE', // AICX 세션 갱신
  SDK_SESSION_CLEAR: 'SDK_SESSION_CLEAR', // AICX 세션 제거
  SDK_WIDGET_VISIBILITY_CHANGED: 'SDK_WIDGET_VISIBILITY_CHANGED', // 부모 위젯 노출 상태
  SDK_DELIVERY_ADDRESS_RESPONSE: 'SDK_DELIVERY_ADDRESS_RESPONSE',

  // chat-app → SDK 방향
  APP_READY: 'APP_READY', // chat-app 로딩 완료
  APP_CLOSE_REQUEST: 'APP_CLOSE_REQUEST', // 닫기 요청 (X 버튼 클릭)
  APP_PONG: 'APP_PONG', // PING에 대한 응답
  APP_CHAT_ID_CHANGED: 'APP_CHAT_ID_CHANGED', // 채팅방 ID 변경 알림
  APP_UNREAD_REFRESH_REQUIRED: 'APP_UNREAD_REFRESH_REQUIRED', // 고객 읽음 서버 반영 알림
  APP_CHAT_STATUS: 'APP_CHAT_STATUS', // 채팅방 상태 변경 알림
  APP_OPEN_URL: 'APP_OPEN_URL', // 외부 URL 열기 요청 (Android WebView 대응)
  APP_SESSION_REQUIRED: 'APP_SESSION_REQUIRED', // AICX 세션 재발급 요청
  APP_CHAT_RESET_REQUEST: 'APP_CHAT_RESET_REQUEST', // 현재 대화를 벗어나 새 문의 시작 요청
  APP_DELIVERY_ADDRESS_REQUEST: 'APP_DELIVERY_ADDRESS_REQUEST',
} as const;

// ========================================
// SDK → chat-app 메시지 타입
// ========================================

/**
 * SDK가 chat-app에 초기 설정 전달
 * iframe 로드 완료 후 APP_READY 메시지를 받으면 전송
 */
export interface SDKInitMessage {
  type: typeof MESSAGE_TYPES.SDK_INIT;
  payload: {
    /** 테마 설정 */
    theme: 'light' | 'dark';
    /** 디버그 모드 활성화 여부 */
    debug: boolean;
    /** SDK가 실행 중인 부모 window의 origin (chat-app이 응답 시 targetOrigin으로 사용) */
    origin: string;
    /** SDK가 실행 중인 부모 페이지의 전체 URL (인증 후 원래 화면 복귀용) */
    hostPageUrl?: string;
    /** 서비스 키 (tenant_public_id) */
    serviceKey: string;
    /** AICC 위젯 화면 범위. 누락은 기존 전체 고객센터를 뜻함 */
    surface?: 'support-center' | 'conversation';
    /** SDK가 iframe URL을 조립한 chatAppUrl의 pathname */
    chatAppPathname?: string;
    /** AICX API 인증 세션 */
    aicxSessionToken?: string;
    /** API/동작 환경 */
    environment?: 'development' | 'stage' | 'production';
    /** 사용자 식별자 (미지정 시 chat-app에서 자동 생성) */
    userId?: string;
    /** 분석 이벤트용 사용자 키 */
    userKey?: string;
    /** 고객센터와 채팅 이벤트를 연결할 여정 UUID */
    journeyId?: string;
    /** 채팅 UI에 표시할 챗봇 이름 */
    botName?: string;
    /** 채팅 UI에 표시할 챗봇 프로필 이미지 URL */
    botProfileImage?: string;
    /** 사전 컨텍스트 (첫 메시지에 포함될 정보) */
    preContext?: string;
    /** 새 채팅 첫 요청의 필수 조회에 사용할 구조화된 문자열 문맥 */
    clientContext?: AICXClientContext;
    /** chat-app이 먼저 표시할 초기 봇 메시지 */
    initialBotMessage?: string;
    /** 채팅 본문 배경색 */
    chatBackgroundColor?: string;
    /** 봇 말풍선 배경색 */
    botBubbleBackgroundColor?: string;
    /** 사용자 말풍선 배경색 */
    userBubbleBackgroundColor?: string;
    /** AICC 위젯 전용 고객사 표시 설정 */
    saasWidgetDisplayConfig?: SaaSWidgetDisplayConfig;
    /** 이전 세션의 채팅방 ID (재초기화 시 히스토리 복원용) */
    chatId?: string;
    /** 부모 위젯 노출 상태 변경 메시지를 지원하는 SDK */
    widgetVisibilityEvents?: true;
    /** 안 읽음 조회와 읽음 완료 공개 이벤트를 지원하는 SDK */
    unreadMessageIntegration?: true;
    /** 기존 인증 주체를 유지한 채 iframe의 현재 채팅만 초기화 */
    resetChatSession?: true;
  };
}

/**
 * SDK가 chat-app에 테마 변경 요청
 * 런타임에 테마를 동적으로 변경할 때 사용
 */
export interface SDKThemeChangeMessage {
  type: typeof MESSAGE_TYPES.SDK_THEME_CHANGE;
  payload: {
    theme: 'light' | 'dark';
  };
}

/**
 * SDK가 chat-app에 연결 확인 (ping)
 * chat-app이 정상 작동 중인지 확인할 때 사용
 */
export interface SDKPingMessage {
  type: typeof MESSAGE_TYPES.SDK_PING;
}

/**
 * SDK가 chat-app에 채팅 메시지 전송 요청
 * 고객사가 chatbot.sendMessage()를 호출했을 때 전송
 */
export interface SDKSendMessageMessage {
  type: typeof MESSAGE_TYPES.SDK_SEND_MESSAGE;
  payload: {
    message: string;
  };
}

/** SDK가 chat-app에 재발급 AICX 세션 전달 */
export interface SDKSessionUpdateMessage {
  type: typeof MESSAGE_TYPES.SDK_SESSION_UPDATE;
  payload: {
    aicxSessionToken: string;
  };
}

/** SDK가 chat-app에 AICX 세션 제거 알림 */
export interface SDKSessionClearMessage {
  type: typeof MESSAGE_TYPES.SDK_SESSION_CLEAR;
}

export interface SDKWidgetVisibilityChangedMessage {
  type: typeof MESSAGE_TYPES.SDK_WIDGET_VISIBILITY_CHANGED;
  payload: {
    visible: boolean;
  };
}

export interface SDKDeliveryAddressResponseMessage {
  type: typeof MESSAGE_TYPES.SDK_DELIVERY_ADDRESS_RESPONSE;
  payload: {
    requestId: string;
    address?: AICXDeliveryAddressEditData;
    error?: 'not_configured' | 'not_found' | 'failed';
  };
}

/** SDK → chat-app 으로 보낼 수 있는 모든 메시지 타입 */
export type SDKToAppMessage =
  | SDKInitMessage
  | SDKThemeChangeMessage
  | SDKPingMessage
  | SDKSendMessageMessage
  | SDKSessionUpdateMessage
  | SDKSessionClearMessage
  | SDKWidgetVisibilityChangedMessage
  | SDKDeliveryAddressResponseMessage;

// ========================================
// chat-app → SDK 메시지 타입
// ========================================

/**
 * chat-app이 SDK에 로딩 완료 알림
 * iframe 로드 후 chat-app이 준비되면 가장 먼저 보내는 메시지
 */
export interface AppReadyMessage {
  type: typeof MESSAGE_TYPES.APP_READY;
}

/**
 * chat-app이 SDK에 닫기 요청
 * 사용자가 chat-app 내부의 X 버튼을 클릭했을 때 전송
 */
export interface AppCloseRequestMessage {
  type: typeof MESSAGE_TYPES.APP_CLOSE_REQUEST;
}

/**
 * chat-app이 SDK에 PING 응답
 * SDK의 SDK_PING에 대한 응답
 */
export interface AppPongMessage {
  type: typeof MESSAGE_TYPES.APP_PONG;
}

/**
 * chat-app이 SDK에 채팅방 ID 변경 알림
 * 서버에서 새 chat_id를 할당받았을 때 전송
 */
export interface AppChatIdChangedMessage {
  type: typeof MESSAGE_TYPES.APP_CHAT_ID_CHANGED;
  payload: {
    chatId: string;
  };
}

export interface AppUnreadRefreshRequiredMessage {
  type: typeof MESSAGE_TYPES.APP_UNREAD_REFRESH_REQUIRED;
  payload: {
    chatId: string;
    reason: 'read';
  };
}

/**
 * chat-app이 SDK에 채팅방 상태 알림
 * open/closed 상태 동기화 및 종료 사유 전달에 사용
 */
export interface AppChatStatusMessage {
  type: typeof MESSAGE_TYPES.APP_CHAT_STATUS;
  payload: {
    chatId: string;
    status: string;
    closeReason?: string | null;
  };
}

/**
 * chat-app이 SDK에 외부 URL 열기 요청
 * Android WebView에서 iframe 내부의 window.open()이 차단될 때
 * 부모 window(SDK)에게 URL 열기를 위임하는 용도
 */
export interface AppOpenUrlMessage {
  type: typeof MESSAGE_TYPES.APP_OPEN_URL;
  payload: {
    url: string;
    /**
     * chat-app 내부 첨부파일 다운로드처럼 "열기"가 아니라 파일 저장 UX가 필요한 요청.
     * 앱 WebView에서는 iframe 안의 a[download]/blob 다운로드가 막히거나 크래시가 날 수 있어
     * SDK 부모 window가 다운로드 가능한 HTTPS URL을 대신 처리한다.
     */
    download?: boolean;
    /** download=true일 때 OS/브라우저 다운로드 UI에 넘길 파일명 힌트 */
    fileName?: string;
    /**
     * URL을 열 브라우징 컨텍스트.
     * - _blank: 외부 링크/새 탭 이동
     * - _self: 로그인/인증처럼 기존 탭 복귀 맥락을 유지해야 하는 이동
     */
    target?: '_blank' | '_self';
  };
}

/** chat-app이 SDK에 AICX 세션 재발급 요청 */
export interface AppSessionRequiredMessage {
  type: typeof MESSAGE_TYPES.APP_SESSION_REQUIRED;
  payload: {
    reason: 'expired' | 'missing' | 'invalid';
  };
}

/** chat-app이 SDK에 저장된 현재 chatId를 지우고 새 문의 상태로 전환해 달라고 요청 */
export interface AppChatResetRequestMessage {
  type: typeof MESSAGE_TYPES.APP_CHAT_RESET_REQUEST;
}

export interface AppDeliveryAddressRequestMessage {
  type: typeof MESSAGE_TYPES.APP_DELIVERY_ADDRESS_REQUEST;
  payload: {
    requestId: string;
    orderId: string;
    bundleId: string;
    orderOptionId?: string;
  };
}

/** chat-app → SDK 로 보낼 수 있는 모든 메시지 타입 */
export type AppToSDKMessage =
  | AppReadyMessage
  | AppCloseRequestMessage
  | AppPongMessage
  | AppChatIdChangedMessage
  | AppUnreadRefreshRequiredMessage
  | AppChatStatusMessage
  | AppOpenUrlMessage
  | AppSessionRequiredMessage
  | AppChatResetRequestMessage
  | AppDeliveryAddressRequestMessage;
